import React, { useState } from "react";
import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import api from "../lib/api";

// Reusable "Report Error" / "Report a bug" control. Self-contained (no toast/context
// dependency) so it works even inside a crashed React tree (ErrorBoundary fallback).
const ReportErrorButton = ({
  context = "page",
  message = "",
  stack = "",
  label = "Report error",
  className = "",
  testId = "report-error-btn",
}) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(false);
    try {
      await api.post("/error-reports", {
        context,
        url: typeof window !== "undefined" ? window.location.href : "",
        message: [message, note].filter(Boolean).join(" | ").slice(0, 2000),
        stack: String(stack || "").slice(0, 4000),
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      });
      setDone(true);
    } catch (e) {
      setError(true);
    }
    setBusy(false);
  };

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => { setOpen(true); setDone(false); setError(false); setNote(""); }}
        className={
          className ||
          "inline-flex items-center gap-2 text-neutral-300 font-semibold px-6 py-3 rounded-xl border border-[#2A2A2A] hover:border-[#FF5C00]/50 bg-[#181818] transition-colors"
        }
      >
        <AlertTriangle size={16} className="text-[#FF7A2F]" /> {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          data-testid="report-error-modal"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-[440px] rounded-2xl border border-[#2A2A2A] bg-[#1A1A1A] p-7 text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-bold text-white">Report this error</h3>
              <button data-testid="report-error-close" onClick={() => setOpen(false)} className="text-neutral-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            {done ? (
              <div className="mt-6 text-center py-4" data-testid="report-error-success">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#1A8A4A]/15 border border-[#1A8A4A]/40 mb-3">
                  <Check size={24} className="text-[#22C55E]" />
                </div>
                <p className="text-white font-semibold">Thanks, we got it.</p>
                <p className="text-neutral-400 text-sm mt-1">Our team has been notified and will look into it.</p>
                <button onClick={() => setOpen(false)} className="mt-5 text-sm text-neutral-300 hover:text-white underline">Close</button>
              </div>
            ) : (
              <>
                <p className="text-neutral-400 text-sm mt-2">
                  Tell us what you were trying to do (optional). We'll include the page address automatically.
                </p>
                <textarea
                  data-testid="report-error-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What went wrong?"
                  rows={3}
                  className="w-full mt-4 px-4 py-3 rounded-lg bg-[#0D0D0D] border border-[#2A2A2A] text-white text-[15px] placeholder-neutral-600 focus:outline-none focus:border-[#FF5C00] transition-colors resize-none"
                />
                {error && <p className="text-red-400 text-sm mt-2" data-testid="report-error-fail">Could not send the report. Please try again.</p>}
                <button
                  data-testid="report-error-submit"
                  onClick={submit}
                  disabled={busy}
                  style={{ backgroundColor: "#1A8A4A" }}
                  className="w-full mt-4 inline-flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl hover:brightness-110 transition-all disabled:opacity-60"
                >
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <AlertTriangle size={16} />}
                  {busy ? "Sending..." : "Send report"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReportErrorButton;
